/**
 * 视频组的聚合、加入、合并、移除与排序（多对象联动的原子图操作）。
 *
 * 与图片组的差异：
 *  - 画布视频一律是持久线上地址（不允许 blob），所以吸收前不需要「远程化」这一步；
 *  - 视频组没有生成槽位，只吸收已生成完成（status === 'completed'）且有地址的视频节点；
 *  - 多出「组内排序」：数组顺序即拼接顺序，可前移/后移调整。
 *
 * 全部经 boardActions.applyGraphPatch 单步提交（单条撤销历史），
 * 连线端点替换共用 buildAbsorbRewire（与图片组同一套规则）。
 */
import { boardActions, getBoardState } from '../state/boardStore';
import { makeNode, nid } from '../utils';
import { DEFAULT_NODE_SIZES, VIDEO_GROUP_MAX_CLIPS } from '../constants';
import toast from '../../../components/Toast';
import type {
  CanvasEdge,
  CanvasNode,
  VideoGroupGeneratingSlot,
  VideoGroupNodeData,
  VideoNodeData,
} from '../types';

/** 组内单个视频槽位 */
interface VideoSlot {
  url: string;
  coverUrl: string | null;
  durationSec: number | null;
}

/** 取视频节点可入组的槽位；不可入组（生成中 / 无地址 / 会话级地址）返回 null */
function videoSlotOf(node: CanvasNode): VideoSlot | null {
  if (node.type !== 'video') return null;
  const data = node.data as VideoNodeData;
  const url = data.videoUrl;
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return null;
  if (data.status && data.status !== 'completed') return null;
  return {
    url,
    coverUrl: data.coverUrl ?? null,
    durationSec: typeof data.videoDurationSec === 'number' ? data.videoDurationSec : null,
  };
}

/**
 * 被吸收节点（removedIds）的所有连线端点替换为 absorbIntoId：
 * 两端都被吸收则丢弃，替换后按 source->target 去重。
 */
function buildAbsorbRewire(
  edges: CanvasEdge[],
  removedIds: Set<string>,
  absorbIntoId: string
): CanvasEdge[] {
  const touched = edges.filter(
    (e) => removedIds.has(e.sourceNodeId) || removedIds.has(e.targetNodeId)
  );
  const seen = new Set<string>();
  const rewired: CanvasEdge[] = [];
  for (const e of touched) {
    const source = removedIds.has(e.sourceNodeId) ? absorbIntoId : e.sourceNodeId;
    const target = removedIds.has(e.targetNodeId) ? absorbIntoId : e.targetNodeId;
    if (source === target) continue;
    const key = `${source}->${target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rewired.push({
      id: nid(),
      sourceNodeId: source,
      targetNodeId: target,
      sourceHandle: 'right',
      targetHandle: 'left',
    });
  }
  return rewired;
}

/** 组数据三方数组按槽位索引对齐取齐（历史数据可能缺封面/时长） */
function readGroupSlots(data: VideoGroupNodeData): {
  urls: string[];
  covers: (string | null)[];
  durations: (number | null)[];
} {
  const urls = [...(data.videoUrls ?? [])];
  return {
    urls,
    covers: urls.map((_, i) => (data.coverUrls ?? [])[i] ?? null),
    durations: urls.map((_, i) => (data.durations ?? [])[i] ?? null),
  };
}

/** 多个视频节点合并为新建视频组（组放选中节点包围盒中心） */
export function aggregateSelectedVideoNodes(nodeIds: string[]): void {
  const state = getBoardState();
  const idSet = new Set(nodeIds);
  const videos = state.nodes.filter((n) => n.type === 'video' && idSet.has(n.id));
  if (videos.length < 2) {
    toast.error('请至少选择 2 个视频节点再合并');
    return;
  }
  if (videos.length > VIDEO_GROUP_MAX_CLIPS) {
    toast.error(`一个视频组最多 ${VIDEO_GROUP_MAX_CLIPS} 段视频`);
    return;
  }

  const slots = videos.map(videoSlotOf);
  const badIdx = slots.findIndex((s) => !s);
  if (badIdx >= 0) {
    toast.error(`第 ${badIdx + 1} 个视频尚未生成完成或地址无效，无法合并`);
    return;
  }

  const minX = Math.min(...videos.map((n) => n.x));
  const minY = Math.min(...videos.map((n) => n.y));
  const maxX = Math.max(...videos.map((n) => n.x + n.width));
  const maxY = Math.max(...videos.map((n) => n.y + n.height));
  const { width: gw, height: gh } = DEFAULT_NODE_SIZES['video_group'];
  const maxZ = Math.max(...videos.map((n) => n.zIndex));
  const absorbedIds = videos.map((n) => n.id);

  const groupNode = makeNode<VideoGroupNodeData>(
    'video_group',
    { x: (minX + maxX) / 2 - gw / 2, y: (minY + maxY) / 2 - gh / 2 },
    {
      videoUrls: slots.map((s) => (s as VideoSlot).url),
      coverUrls: slots.map((s) => (s as VideoSlot).coverUrl),
      durations: slots.map((s) => (s as VideoSlot).durationSec),
    },
    undefined,
    maxZ
  );

  boardActions.applyGraphPatch({
    addNodes: [groupNode],
    removeNodeIds: absorbedIds,
    addEdges: buildAbsorbRewire(state.edges, new Set(absorbedIds), groupNode.id),
  });
  toast.success(`已合并 ${videos.length} 段视频为视频组`);
}

/** 单个视频节点加入已有视频组（追加到末尾，连线转接到组） */
export function addVideoToGroup(videoNodeId: string, groupNodeId: string): void {
  const state = getBoardState();
  const video = state.nodes.find((n) => n.id === videoNodeId);
  const group = state.nodes.find((n) => n.id === groupNodeId);
  if (!video || video.type !== 'video' || !group || group.type !== 'video_group') return;

  const slot = videoSlotOf(video);
  if (!slot) {
    toast.error('视频尚未生成完成或地址无效，无法加入视频组');
    return;
  }

  const gd = group.data as VideoGroupNodeData;
  const { urls, covers, durations } = readGroupSlots(gd);
  if (urls.length >= VIDEO_GROUP_MAX_CLIPS) {
    toast.error(`一个视频组最多 ${VIDEO_GROUP_MAX_CLIPS} 段视频`);
    return;
  }

  boardActions.applyGraphPatch({
    removeNodeIds: [videoNodeId],
    addEdges: buildAbsorbRewire(state.edges, new Set([videoNodeId]), groupNodeId),
    nodeDataPatches: [
      {
        id: groupNodeId,
        data: {
          videoUrls: [...urls, slot.url],
          coverUrls: [...covers, slot.coverUrl],
          durations: [...durations, slot.durationSec],
        },
      },
    ],
  });
  toast.success('已加入视频组');
}

/** 视频组合并：源组各段按序追加到目标组尾部，源组消失，连线转接去重 */
export function mergeVideoGroups(sourceGroupId: string, targetGroupId: string): void {
  if (sourceGroupId === targetGroupId) return;
  const state = getBoardState();
  const source = state.nodes.find((n) => n.id === sourceGroupId);
  const target = state.nodes.find((n) => n.id === targetGroupId);
  if (!source || source.type !== 'video_group' || !target || target.type !== 'video_group') return;

  const sd = readGroupSlots(source.data as VideoGroupNodeData);
  const td = readGroupSlots(target.data as VideoGroupNodeData);
  if (td.urls.length + sd.urls.length > VIDEO_GROUP_MAX_CLIPS) {
    toast.error(`合并后超过 ${VIDEO_GROUP_MAX_CLIPS} 段，无法合并`);
    return;
  }

  boardActions.applyGraphPatch({
    removeNodeIds: [sourceGroupId],
    addEdges: buildAbsorbRewire(state.edges, new Set([sourceGroupId]), targetGroupId),
    nodeDataPatches: [
      {
        id: targetGroupId,
        data: {
          videoUrls: [...td.urls, ...sd.urls],
          coverUrls: [...td.covers, ...sd.covers],
          durations: [...td.durations, ...sd.durations],
        },
      },
    ],
  });
  toast.success(`已合并视频组（共 ${td.urls.length + sd.urls.length} 段）`);
}

/** 组内移除某段视频；移除后组为空则组节点一并移除 */
export function removeVideoFromGroup(groupNodeId: string, index: number): void {
  const state = getBoardState();
  const group = state.nodes.find((n) => n.id === groupNodeId);
  if (!group || group.type !== 'video_group') return;

  const { urls, covers, durations } = readGroupSlots(group.data as VideoGroupNodeData);
  if (index < 0 || index >= urls.length) return;

  const nextUrls = urls.filter((_, i) => i !== index);
  if (nextUrls.length === 0) {
    boardActions.applyGraphPatch({ removeNodeIds: [groupNodeId] });
    toast.success('视频组已空，节点一并移除');
    return;
  }

  boardActions.updateNodeData(groupNodeId, {
    videoUrls: nextUrls,
    coverUrls: covers.filter((_, i) => i !== index),
    durations: durations.filter((_, i) => i !== index),
  });
}

/** 从视频组取出某段为独立视频节点；取空最后一个时组节点一并移除 */
export function extractVideoFromGroup(groupNodeId: string, index: number): void {
  const state = getBoardState();
  const group = state.nodes.find((n) => n.id === groupNodeId);
  if (!group || group.type !== 'video_group') return;

  const { urls, covers, durations } = readGroupSlots(group.data as VideoGroupNodeData);
  if (index < 0 || index >= urls.length) return;

  const url = urls[index];
  const coverUrl = covers[index] ?? null;
  const durationSec = durations[index] ?? null;

  // 新视频节点放组右侧（与"组导出结果"同款摆放惯例）
  const videoNode = makeNode<VideoNodeData>(
    'video',
    { x: group.x + group.width + 60, y: group.y + index * 24 },
    {
      videoUrl: url,
      coverUrl,
      status: 'completed',
      videoDurationSec: durationSec ?? undefined,
      modelLabel: '视频组取出',
    },
    undefined,
    group.zIndex
  );

  const nextUrls = urls.filter((_, i) => i !== index);
  const nextCovers = covers.filter((_, i) => i !== index);
  const nextDurations = durations.filter((_, i) => i !== index);

  // 取空最后一个槽位时组一并移除；否则只更新组数据。单步撤销。
  const groupEmpty = nextUrls.length === 0;
  boardActions.applyGraphPatch({
    addNodes: [videoNode],
    ...(groupEmpty
      ? { removeNodeIds: [groupNodeId] }
      : {
          nodeDataPatches: [
            { id: groupNodeId, data: { videoUrls: nextUrls, coverUrls: nextCovers, durations: nextDurations } },
          ],
        }),
  });
  toast.success(
    groupEmpty ? '已取出为独立视频节点（组已空，视频组一并移除）' : '已取出为独立视频节点'
  );
}

/** 组内调整某段视频的顺序（delta = -1 前移 / 1 后移），数组顺序即拼接顺序 */
export function moveVideoInGroup(groupNodeId: string, index: number, delta: -1 | 1): void {
  const state = getBoardState();
  const group = state.nodes.find((n) => n.id === groupNodeId);
  if (!group || group.type !== 'video_group') return;

  const { urls, covers, durations } = readGroupSlots(group.data as VideoGroupNodeData);
  const targetIndex = index + delta;
  if (targetIndex < 0 || targetIndex >= urls.length) return;

  const swap = <T,>(arr: T[], a: number, b: number) => {
    const tmp = arr[a];
    arr[a] = arr[b];
    arr[b] = tmp;
  };
  swap(urls, index, targetIndex);
  swap(covers, index, targetIndex);
  swap(durations, index, targetIndex);

  boardActions.updateNodeData(groupNodeId, {
    videoUrls: urls,
    coverUrls: covers,
    durations,
  });
}

// ============================================================================
// 生成槽位（生视频节点「一次生成多个」时预填，终态后成功的转正进 videoUrls）
//
// 槽位与 videoUrls 分离的原因：合并 / 排序 / 移除 / 导出只面对「已完成的段」，
// 不必理解半成品状态；生成中的段仅参与渲染，不参与任何拼接语义。
// ============================================================================

/** 读取视频组节点当前数据；节点被撤销/删除时返回 null */
function readVideoGroup(groupNodeId: string): VideoGroupNodeData | null {
  const node = getBoardState().nodes.find((n) => n.id === groupNodeId);
  return node ? (node.data as VideoGroupNodeData) : null;
}

/** 生成初始槽位数组：全部 processing，等待逐个回填 jobId 与结果 */
export function buildGeneratingSlots(count: number): VideoGroupGeneratingSlot[] {
  return Array.from({ length: Math.max(0, count) }, () => ({
    jobId: null,
    status: 'processing',
  }));
}

/** 按索引写回单个槽位（读-改-写整组数组，同步执行无竞态） */
export function patchVideoGroupSlot(
  groupNodeId: string,
  index: number,
  patch: Partial<VideoGroupGeneratingSlot>
): void {
  const data = readVideoGroup(groupNodeId);
  if (!data) return;
  const slots = [...(data.generatingSlots ?? [])];
  if (index < 0 || index >= slots.length) return;
  slots[index] = { ...slots[index], ...patch };
  boardActions.updateNodeData(groupNodeId, { generatingSlots: slots });
}

/** 移除单个槽位（失败槽位的手动清理入口） */
export function removeVideoGroupSlot(groupNodeId: string, index: number): void {
  const data = readVideoGroup(groupNodeId);
  if (!data) return;
  const slots = [...(data.generatingSlots ?? [])];
  if (index < 0 || index >= slots.length) return;
  slots.splice(index, 1);
  boardActions.updateNodeData(groupNodeId, { generatingSlots: slots });
}

/**
 * 整批终态收敛：本轮槽位里「已完成」的按原顺序追加进 videoUrls
 * （拼接顺序 = 提交顺序），并把它们从槽位中移除；
 * 失败 / 无结果的槽位保留，让用户看到原因并可手动移除。
 *
 * 组内段数上限在提交前已校验，这里只做兜底截断，避免历史数据越界。
 */
export function settleVideoGroupSlots(groupNodeId: string): void {
  const data = readVideoGroup(groupNodeId);
  if (!data) return;
  const slots = data.generatingSlots ?? [];
  if (slots.length === 0) return;

  const done = slots.filter((s) => s.status === 'completed' && s.url);
  const kept = slots.filter((s) => !(s.status === 'completed' && s.url));
  if (done.length === 0) return;

  const { urls, covers, durations } = readGroupSlots(data);
  const room = Math.max(0, VIDEO_GROUP_MAX_CLIPS - urls.length);
  const accepted = done.slice(0, room);
  // 被截断的槽位不返回生成方：它们已是成品，保留槽位形态供用户自行处理
  const overflow = done.slice(room);
  if (overflow.length > 0) {
    toast.error(`视频组最多 ${VIDEO_GROUP_MAX_CLIPS} 段，超出部分未加入`);
  }

  boardActions.updateNodeData(groupNodeId, {
    videoUrls: [...urls, ...accepted.map((s) => s.url as string)],
    coverUrls: [...covers, ...accepted.map((s) => s.coverUrl ?? null)],
    durations: [
      ...durations,
      ...accepted.map((s) => (typeof s.durationSec === 'number' ? s.durationSec : null)),
    ],
    generatingSlots: [...overflow, ...kept],
  });
}
